import {
  RefObject,
  SelectHTMLAttributes,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { DayPicker } from 'react-day-picker'
import { kk, ru } from 'react-day-picker/locale'
import { useTranslation } from 'react-i18next'
import { ReactSVG } from 'react-svg'
import { useOnClickOutside } from 'usehooks-ts'

import { cn } from '@shared/lib/cn'
import Wrapper from '@shared/ui/wrapper'

import styles from './styles.module.scss'

import type {
  ClassNames,
  CustomComponents,
  DropdownOption,
  Matcher,
} from 'react-day-picker'


import './datepicker.scss'


interface CustomDropdownProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  components: CustomComponents;
  classNames: ClassNames;
  options?: DropdownOption[];
}

const CustomDropdown = (props: CustomDropdownProps) => {
  const { t: tm } = useTranslation('month')
  const { value, onChange, options = [] } = props
  const [ open, setOpen ] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)
  const selectedRef = useRef<HTMLButtonElement | null>(null)

  const handleSelect = (val: number) => {
    if (onChange) {
      const event = {
        target: { value: val },
      } as unknown as React.ChangeEvent<HTMLSelectElement>
      onChange(event)
    }
    setOpen(false)
  }

  const handleOutsideClick = () => {
    setOpen(false)
  }

  useOnClickOutside(listRef as RefObject<HTMLDivElement>, handleOutsideClick)

  useEffect(() => {
    if (open && selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: 'center' })
    }
  }, [ open ])

  return (
    <div className={styles['custom-dropdown']} ref={listRef}>
      <button
        type="button"
        className={cn(styles['custom-dropdown__trigger'], 'reset-button')}
        onClick={() => setOpen((prev) => !prev)}
      >
        {tm(String(value))}
        <ReactSVG
          className={styles['custom-dropdown__icon']}
          src="/icons/arrow.svg"
          wrapper="span"
        />
      </button>

      {open && (
        <div className={styles['custom-dropdown__list']}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={cn(styles['custom-dropdown__option'], 'reset-button', {
                [styles.selected]: option.value === value,
              })}
              onClick={() => handleSelect(option.value)}
              ref={option.value === value ? selectedRef : null}
            >
              {tm(String(option.value))}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

interface DatepickerProps {
  value?: Date;
  startMonth?: Date;
  endMonth?: Date;
  disabled?: Matcher;
  onChange?: (date: Date | undefined) => void;
  onClose?: () => void;
}

export default function Datepicker ({
  value,
  startMonth = new Date(1900, 0),
  endMonth,
  disabled,
  onChange,
  onClose,
}: DatepickerProps) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [ internalDate, setInternalDate ] = useState<Date | undefined>(value)

  useEffect(() => {
    if (value !== undefined) {
      setInternalDate(value)
    }
  }, [ value ])

  const today = new Date()
  const defaultEndDate = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate(),
  )

  const handleSelect = (date: Date | undefined) => {
    if (onChange) {
      onChange(date)
    }
    else {
      setInternalDate(date)
    }
  }

  const handleOutsideClick = () => {
    if (onClose) {
      onClose()
    }
  }

  const { i18n } = useTranslation()

  const dayPickerLocale = useMemo(() => {
    switch (i18n.language) {
    case 'ru':
      return ru

    case 'kk':
      return kk

    default:
      return ru
    }
  }, [ i18n ])

  useOnClickOutside(wrapperRef as RefObject<HTMLElement>, handleOutsideClick)

  return (
    <Wrapper className={styles.wrapper} ref={wrapperRef}>
      <DayPicker
        locale={dayPickerLocale}
        mode="single"
        selected={value ?? internalDate}
        onSelect={handleSelect}
        startMonth={startMonth}
        endMonth={
          endMonth ??
          new Date(defaultEndDate.getFullYear(), defaultEndDate.getMonth())
        }
        disabled={disabled ?? { after: defaultEndDate }}
        captionLayout="dropdown"
        components={{
          Dropdown: CustomDropdown,
        }}
        defaultMonth={value ?? internalDate}
      />
    </Wrapper>
  )
}
